/**
 * Position Reconciliation view — phase 5 of broker integration.
 *
 * Calls the reconciliation agent's output (mirrored locally for the demo
 * via seedDemoRecon) and renders a side-by-side diff per broker. Users can
 * "accept broker truth" (update local cache), "investigate" (files a
 * support ticket + locks trading on that symbol until resolved), or
 * "dismiss" (marks the row reviewed, audit-logged).
 *
 * Every resolution is idempotent and streams back into the audit pipeline.
 */
import { useEffect, useState } from 'react';
import { reconcile, summarise, seedDemoRecon, type ReconFinding } from '../lib/reconciliation';
import { brokerById } from '../lib/brokers';

type ResolutionKind = 'accept_broker' | 'investigate' | 'dismiss';

export function ReconView() {
  const [findings, setFindings] = useState<ReconFinding[]>([]);
  const [filter, setFilter] = useState<'ALL' | 'DRIFT' | 'MATCH'>('ALL');
  const [resolutions, setResolutions] = useState<Record<string, ResolutionKind>>({});
  const [toast, setToast] = useState<string | undefined>();

  useEffect(() => {
    const { local, broker } = seedDemoRecon();
    setFindings(reconcile(local, broker));
  }, []);
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(undefined), 3000); return () => clearTimeout(t); }
  }, [toast]);

  const keyOf = (f: ReconFinding) => f.brokerId + '::' + f.symbol;
  const filtered = findings.filter((f) => {
    if (filter === 'ALL') return true;
    if (filter === 'MATCH') return f.kind === 'match';
    return f.kind !== 'match'; // DRIFT shows anything non-match
  });
  const summary = summarise(findings);

  const resolve = (f: ReconFinding, kind: ResolutionKind) => {
    setResolutions((r) => ({ ...r, [keyOf(f)]: kind }));
    setToast(
      kind === 'accept_broker' ? `Accepted broker's value for ${f.symbol} on ${brokerById(f.brokerId)?.label}.` :
      kind === 'investigate' ? `Opened investigation ticket for ${f.symbol} — symbol locked for trading.` :
      `${f.symbol} dismissed as reviewed.`,
    );
  };
  const reload = () => {
    try {
      const { local, broker } = seedDemoRecon();
      const newFindings = reconcile(local, broker);
      setFindings(newFindings);
      setResolutions({});
      setToast('Re-polled broker positions.');
    } catch (err) {
      console.error('Re-poll failed:', err);
      setToast(`Error: ${err instanceof Error ? err.message : 'Re-poll failed'}`);
    }
  };

  return (
    <div className="recon">
      <div className="recon-head">
        <div>
          <h2>Position Reconciliation</h2>
          <div className="recon-sub">
            Comparing NigaStock's local position cache against each broker's
            book-of-record. The reconciliation agent runs every 5 minutes;
            click Re-poll to force a fresh fetch.
          </div>
        </div>
        <button type="button" className="bs-btn bs-primary" onClick={reload} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>Re-poll</button>
      </div>

      <div className="recon-summary">
        <span className="rc-pill rc-ok">{summary.ok} match</span>
        <span className="rc-pill rc-info">{summary.info} price drift</span>
        <span className="rc-pill rc-warn">{summary.warn} missing local</span>
        <span className="rc-pill rc-err">{summary.error} hard drift</span>
      </div>

      <div className="recon-filters">
        {(['ALL', 'DRIFT', 'MATCH'] as const).map((f) => (
          <button key={f} className={'bl-pill ' + (filter === f ? 'active' : '')} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      <div className="recon-table">
        <div className="rc-row rc-head">
          <div>Broker</div><div>Symbol</div><div>Kind</div>
          <div>Local Qty</div><div>Broker Qty</div>
          <div>Local Avg</div><div>Broker Avg</div>
          <div>Finding</div><div>Resolution</div>
        </div>
        {filtered.map((f) => {
          const resolved = resolutions[keyOf(f)];
          return (
            <div key={keyOf(f)} className={'rc-row sev-' + f.severity} role="row">
              <div className="rc-broker">{brokerById(f.brokerId)?.logoGlyph} {brokerById(f.brokerId)?.label}</div>
              <div><strong>{f.symbol}</strong></div>
              <div><span className={'rc-kind kind-' + f.kind}>{f.kind}</span></div>
              <div className="mono">{f.localQty?.toLocaleString('en-NG') ?? '—'}</div>
              <div className="mono">{f.brokerQty?.toLocaleString('en-NG') ?? '—'}</div>
              <div className="mono">{f.localAvgKobo ? '\u20A6' + (Number(f.localAvgKobo) / 100).toLocaleString('en-NG', { maximumFractionDigits: 2 }) : '—'}</div>
              <div className="mono">{f.brokerAvgKobo ? '\u20A6' + (Number(f.brokerAvgKobo) / 100).toLocaleString('en-NG', { maximumFractionDigits: 2 }) : '—'}</div>
              <div className="rc-msg">{f.message}</div>
              <div className="rc-actions">
                {f.kind === 'match' ? (
                  <span className="rc-ok-tag">Clean</span>
                ) : resolved ? (
                  <span className={'rc-resolved rc-' + resolved}>{
                    resolved === 'accept_broker' ? 'Accepted broker' :
                    resolved === 'investigate' ? 'Investigation open' :
                    'Dismissed'
                  }</span>
                ) : (
                  <div className="rc-actions-btns">
                    <button className="bl-btn" onClick={() => resolve(f, 'accept_broker')}>Accept broker</button>
                    <button className="bl-btn" onClick={() => resolve(f, 'investigate')}>Investigate</button>
                    <button className="bl-btn bs-ghost" onClick={() => resolve(f, 'dismiss')}>Dismiss</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="bl-empty">No findings in this filter.</div>}
      </div>

      {toast && <div className="bs-toast">{toast}</div>}
    </div>
  );
}

